import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const BASE_URL = "https://www.peptidesformedspas.org";
const SITE_NAME = "PeptidesForMedSpas";

// Titles capped to ~60 characters so Google/Bing don't truncate them in SERPs.
// Descriptions kept under ~160 characters for the same reason.
const PAGE_SEO = {
  "/": {
    title: "Med Spa Research Peptides | PeptidesForMedSpas",
    description: "Curated catalog and plain-English education on med spa research peptides: GHK-Cu, Glutathione, NAD+, BPC-157, TB-500, PT-141 and more. Not medical advice."
  },
  "/shop": {
    title: "Shop Med Spa Peptides | Research Catalog",
    description: "Browse 11 research peptides for med spa, aesthetic, longevity, and wellness research: GHK-Cu, Glutathione, NAD+, BPC-157, TB-500, PT-141 and more."
  },
  "/recovery-guide": {
    title: "Wellness & Recovery Guide | PeptidesForMedSpas",
    description: "Free wellness guide for med spa audiences: sleep, sun protection, nutrition, basic skincare, and active recovery. The high-ROI levers that beat any add-on."
  },
  "/blog": {
    title: "Peptide Research Blog | PeptidesForMedSpas",
    description: "Evidence-based articles on peptide science: GHK-Cu, NAD+, glutathione, sermorelin, PT-141, recovery peptides, regulation, and evaluating peptide claims."
  },
  "/about": {
    title: "About PeptidesForMedSpas | Mission & Disclosures",
    description: "Learn about PeptidesForMedSpas — an educational storefront focused on peptides studied in med spa, aesthetic, longevity, and wellness research."
  },
  "/peptides-for-med-spas": {
    title: "Peptides for Med Spas | Research Catalog Hub",
    description: "The most complete med spa peptide research catalog: anti-aging, weight loss, recovery, hormone, and sexual wellness peptides. Plain-English science."
  },
  "/anti-aging-peptides": {
    title: "Anti-Aging Peptides | GHK-Cu, Glutathione, NAD+",
    description: "Anti-aging research peptides studied in med spa and dermatology contexts: GHK-Cu, glutathione, NAD+. Evidence summaries, mechanisms, and red flags."
  },
  "/weight-loss-peptides": {
    title: "Weight Loss Peptides | Evidence & GLP-1 Context",
    description: "What the research says about weight loss peptides in med spa contexts. 5-Amino-1MQ, GLP-1 regulatory context, evidence tiers, and marketing red flags."
  },
  "/recovery-peptides": {
    title: "Recovery Peptides | BPC-157, TB-500 & PDA",
    description: "Research-grade recovery peptides studied in tissue, tendon, and connective-tissue research models: BPC-157, TB-500, Pentadeca Arginate. Evidence summaries."
  },
  "/hormone-peptides": {
    title: "Hormone Peptides | Sermorelin (GHRH) & MOTS-c",
    description: "Hormone-axis and longevity research peptides: Sermorelin (GHRH 1-29 analog) and MOTS-c (mitochondrial-derived peptide). Evidence overviews and limitations."
  },
  "/sexual-wellness-peptides": {
    title: "Sexual Wellness Peptides | PT-141 Research",
    description: "PT-141 (bremelanotide) — melanocortin receptor agonist studied in pharmacology research. Approved-drug vs research-only distinctions and mechanisms."
  },
  "/peptide-glossary": {
    title: "Peptide Glossary | Definitions & Med Spa Reference",
    description: "Plain-English definitions for peptides, classes, mechanisms, and regulatory terms used in med spa, aesthetic, and longevity research conversations."
  },
  "/partners": {
    title: "Partners | AlphaSync Rx & MyDose.ai",
    description: "Educational referral partners for licensed clinicians and pharmacy fulfillment: AlphaSync Rx (dispensing infrastructure) and MyDose.ai (peptide-therapy platform)."
  },
  "/compliance": {
    title: "Compliance & Regulatory | FDA · FTC · DEA · State",
    description: "PeptidesForMedSpas compliance posture across FDA, FTC, DEA, DSHEA, HIPAA, state med-spa rules, and 503A/503B compounding context. Educational storefront."
  },
  "/provider-access": {
    title: "Provider Access | Verified-Provider Peptide Pricing",
    description: "Apply for verified-provider access through Alpha BioMed Labs for institutional pricing on research-grade peptides. Multi-unit tiers for med spas and clinics."
  },
  "/topical-peptides": {
    title: "Topical Peptides | Med Spa Research Catalog",
    description: "Topical research peptides for cosmetic and dermatology research: GHK-Cu, Argireline, Matrixyl, Snap-8, and topical glutathione. Research-only."
  },
  "/affiliate-program": {
    title: "Become an Affiliate | Paramount Peptides Partner",
    description: "Earn commission by promoting research-grade peptides. Built for med spas, content creators, gyms, podcasters, and wellness brands. Free 5-minute application."
  },
  "/sitemap": {
    title: "Sitemap | PeptidesForMedSpas Site Directory",
    description: "Human-readable directory of every page on PeptidesForMedSpas — pillar categories, full research catalog, blog, partner programs, and compliance policies."
  },
  "/locations": {
    title: "Med Spa Peptides by State | All 50 States + DC",
    description: "Research peptides studied in med spa contexts across all 50 U.S. states + DC. Each state includes categories, regulatory context, and medical-board details."
  },
  "/for-med-spas": {
    title: "For Med Spas | Compliance-First Peptide Playbook",
    description: "How licensed med spa owners evaluate peptide research categories, vet medical direction, choose pharmacy partners, and structure compliant offerings."
  },
  "/wholesale-peptides-for-clinics": {
    title: "Wholesale Peptides for Clinics | Institutional Pricing",
    description: "Institutional-volume research-grade peptide sourcing for med spas, longevity clinics, telehealth, and research labs via Alpha BioMed Labs. Research-only."
  },
  "/peptide-protocols-for-aesthetic-practices": {
    title: "Peptide Protocols | Aesthetic-Practice Reference",
    description: "Research-literature overviews of peptide categories most often referenced in aesthetic-practice education. Not clinical protocols. Educational only."
  },
  "/for-providers": {
    title: "For Licensed Providers | Peptide Research Education",
    description: "Evidence-forward peptide research education for NPs, PAs, MDs, and DOs in med spa, aesthetic, longevity, and wellness settings. Evidence tiers and compliance."
  },
  "/editorial-standards": {
    title: "Editorial Standards | Evidence Tiers & Methodology",
    description: "How PeptidesForMedSpas creates, reviews, and maintains educational content. Three-tier evidence framework, review process, and things we never publish."
  },
  "/press": {
    title: "Press & Media Kit | PeptidesForMedSpas",
    description: "Reporter-ready press kit: editorial contact, boilerplate, on-record quotes on med spa peptide regulation, PT-141 vs bremelanotide, and AI-search visibility."
  },
  "/glossary": {
    title: "Peptide Glossary | Free Structured JSON Fact Sheets",
    description: "Citation-ready peptide fact sheets for every compound in our catalog. Free structured JSON at /api/glossary/{slug}. No API key. Free to cite with attribution."
  },
  "/tools/state-regulatory-checker": {
    title: "State Regulatory Checker | Free Med Spa Peptide Tool",
    description: "Pick your state — get the medical board, delegation framework, and med spa peptide regulatory context in one click. All 50 states + DC. Not legal advice."
  },
  "/disclaimer": {
    title: "Disclaimer | Not Medical Advice",
    description: "Full legal disclaimer for PeptidesForMedSpas. Products are research chemicals for laboratory use only. Not intended to diagnose, treat, or cure any disease."
  },
  "/privacy": {
    title: "Privacy Policy | PeptidesForMedSpas",
    description: "PeptidesForMedSpas privacy policy. How we collect, use, and protect personal information when you visit our educational storefront."
  },
  "/terms": {
    title: "Terms of Service | PeptidesForMedSpas",
    description: "Terms and conditions for using PeptidesForMedSpas. Review our policies on purchases, returns, and website usage."
  },
  "/refund": {
    title: "Refund Policy | PeptidesForMedSpas",
    description: "PeptidesForMedSpas refund policy. Conditions for returns on unopened products and how to submit a refund request."
  },
  "/shipping": {
    title: "Shipping Policy | PeptidesForMedSpas",
    description: "Shipping information for PeptidesForMedSpas orders. Delivery timelines, methods, and coverage."
  }
};

function setMetaTag(name, content, property = false) {
  const attr = property ? "property" : "name";
  let tag = document.querySelector(`meta[${attr}="${name}"]`);
  if (tag) {
    tag.setAttribute("content", content);
  } else {
    tag = document.createElement("meta");
    tag.setAttribute(attr, name);
    tag.setAttribute("content", content);
    document.head.appendChild(tag);
  }
}

function setCanonical(url) {
  let link = document.querySelector('link[rel="canonical"]');
  if (link) {
    link.setAttribute("href", url);
  } else {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    link.setAttribute("href", url);
    document.head.appendChild(link);
  }
}

// Set (or update) a per-page hreflang alternate. Using a single-language site
// so each page points to itself for en-US and x-default — resolves the "hreflang
// tags conflict" auditor flag caused by a static index.html tag that always
// pointed to `/` regardless of the actual page URL.
function setHreflang(hreflangValue, url) {
  let link = document.querySelector(
    `link[rel="alternate"][hreflang="${hreflangValue}"]`
  );
  if (link) {
    link.setAttribute("href", url);
  } else {
    link = document.createElement("link");
    link.setAttribute("rel", "alternate");
    link.setAttribute("hreflang", hreflangValue);
    link.setAttribute("href", url);
    document.head.appendChild(link);
  }
}

export function usePageSEO(customTitle, customDescription) {
  const location = useLocation();
  const path = location.pathname;

  useEffect(() => {
    const seo = PAGE_SEO[path];

    const title = customTitle || (seo ? seo.title : `${SITE_NAME} | Med Spa Research Peptides`);
    const description = customDescription || (seo ? seo.description : "Curated catalog and plain-English education on med spa research peptides. GHK-Cu, Glutathione, NAD+, BPC-157, TB-500, PT-141 and more. Not medical advice.");
    const canonicalUrl = `${BASE_URL}${path}`;

    document.title = title;
    setMetaTag("description", description);
    setMetaTag("og:title", title, true);
    setMetaTag("og:description", description, true);
    setMetaTag("og:url", canonicalUrl, true);
    setMetaTag("twitter:title", title);
    setMetaTag("twitter:description", description);
    setCanonical(canonicalUrl);
    setHreflang("en-US", canonicalUrl);
    setHreflang("x-default", canonicalUrl);
  }, [path, customTitle, customDescription]);
}

export default usePageSEO;
